import { Container, Row, Col, Form, Card } from 'react-bootstrap';
import { useState } from 'react';
import { RiSparklingFill } from '@remixicon/react';
import Sparkle from '../Sparkle/Sparkle';
import { useTranslation } from "react-i18next";
import SpinnerButton from '../Button/SpinnerButton';
import { useLocation } from 'react-router-dom';

function GeneratorForm({
    onSubmit,
    title = "product.generateProductSheet",
    placeholderSubject = "product.placeholderSubject",
    placeholderKeywords = "product.placeholderKeywords",
    isLoading = false
}) {
    const location = useLocation();
    const subjectState = location.state?.subject;
    const languageState = location.state?.language;
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        language: languageState || 'french',
        tone: 'neutral',
        contentLength: 'short',
        subject: subjectState || '',
        keywords: ''
    });
    const toneOptions = t('generatorForm.tone.options', { returnObjects: true });
    const contentLengthOptions = t('generatorForm.contentLength.options', { returnObjects: true });
    const languageOptions = t('generatorForm.language.options', { returnObjects: true });
    const isFormValid = formData.language && formData.tone && formData.contentLength && formData.subject.trim().length >= 10;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        await onSubmit(formData);
    };

    return (
        isLoading ? <Sparkle /> : (
            <Container className="py-5">
                <Row className="mb-5">
                    <Col xs={12} className="text-center">
                        <h2 className="title mb-2">{t(title)}</h2>
                        <p className="subtitle">{t('generatorForm.subtitle')}</p>
                    </Col>
                </Row>

                <Card>
                    <Card.Body>
                        <Form noValidate onSubmit={handleSubmit} id="productForm">
                            <Row className="mb-4 g-4">
                                <Col xs={12} md={4}>
                                    <Form.Group controlId="language">
                                        <Form.Label>{`${t('app.language')} *`}</Form.Label>
                                        <Form.Select
                                            className={isLoading ? 'disabled' : ''}
                                            name="language"
                                            required
                                            onChange={handleChange}
                                            value={formData.language}
                                            disabled={isLoading}
                                        >
                                            {Object.entries(languageOptions).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Group controlId="tone">
                                        <Form.Label>{`${t('generatorForm.tone.label')} *`}</Form.Label>
                                        <Form.Select
                                            className={isLoading ? 'disabled' : ''}
                                            name="tone"
                                            required
                                            onChange={handleChange}
                                            value={formData.tone}
                                            disabled={isLoading}
                                        >
                                            {Object.entries(toneOptions).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Group controlId="contentLength">
                                        <Form.Label>{`${t('generatorForm.contentLength.label')} *`}</Form.Label>
                                        <Form.Select
                                            className={isLoading ? 'disabled' : ''}
                                            name="contentLength"
                                            required
                                            onChange={handleChange}
                                            value={formData.contentLength}
                                            disabled={isLoading}
                                        >
                                            {Object.entries(contentLengthOptions).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4 g-4">
                                <Col xs={12}>
                                    <Form.Group controlId="subject">
                                        <Form.Label>{`${t('generatorForm.subject')} *`}</Form.Label>
                                        <Form.Control
                                            className={isLoading ? 'disabled' : ''}
                                            as="textarea"
                                            name="subject"
                                            rows={3}
                                            minLength={10}
                                            maxLength={600}
                                            placeholder={t(placeholderSubject)}
                                            required
                                            disabled={isLoading}
                                            style={{ resize: 'none' }}
                                            onChange={handleChange}
                                            value={formData.subject}
                                        />
                                        <div className="form-text">{t('generatorForm.subjectMinLength', { min: 10 })}</div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4 g-4">
                                <Col xs={12}>
                                    <Form.Group controlId="keywords">
                                        <Form.Label>{t('generatorForm.keywords')}</Form.Label>
                                        <span className="form-text ms-2">{t('generatorForm.optional')}</span>
                                        <Form.Control
                                            className={isLoading ? 'disabled' : ''}
                                            type="text"
                                            name="keywords"
                                            maxLength={600}
                                            placeholder={t(placeholderKeywords)}
                                            required={false}
                                            disabled={isLoading}
                                            onChange={handleChange}
                                            value={formData.keywords}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} md={{ span: 6, offset: 6 }}>
                                    <SpinnerButton
                                        type="submit"
                                        className={`bg-pink w-100 ${!isFormValid ? 'disabled' : ''}`}
                                        disabled={isLoading || !isFormValid}
                                        label={<>
                                            <span className="me-2">{t('app.generate')}</span>
                                            <RiSparklingFill size={20} />
                                        </>}
                                    />
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        )
    );
}

export default GeneratorForm;
